import { useEffect, useRef, useState } from 'react';

import type { CanvassingSessionListQuery } from '../../graphql/queries/canvassingSessionList.generated';
import {
  fetchCanvassingSessions,
  toCanvassingSessionRequestError,
  type CanvassingSessionFetchImpl,
  type CanvassingSessionRequestError,
} from './canvassingSessionApi';
import { DEFAULT_CANVASSING_SESSION_FILTERS, type CanvassingSessionFilterState } from './canvassingSessionFilters';

export type CanvassingSessionListItem = CanvassingSessionListQuery['canvassingSessionList']['items'][number];

export const CANVASSING_SESSION_ROWS_PER_PAGE_OPTIONS = [12, 25, 50] as const;
export const DEFAULT_CANVASSING_SESSION_ROWS_PER_PAGE = 25;

export type UseCanvassingSessionListOptions = {
  fetchImpl?: CanvassingSessionFetchImpl;
  filters?: CanvassingSessionFilterState;
  enabled?: boolean;
};

export type UseCanvassingSessionListResult = {
  items: CanvassingSessionListItem[];
  total: number;
  calendarTimeZone: string | null;
  loading: boolean;
  error: CanvassingSessionRequestError | null;
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  refetch: () => void;
};

const createFilterKey = (filters: CanvassingSessionFilterState): string =>
  JSON.stringify([
    filters.dateRange,
    filters.fromDate ?? null,
    filters.throughDate ?? null,
    filters.canvasserId,
    filters.captureStatus,
    filters.processingStatus,
    filters.sort,
  ]);

export const useCanvassingSessionList = ({
  fetchImpl,
  filters = DEFAULT_CANVASSING_SESSION_FILTERS,
  enabled = true,
}: UseCanvassingSessionListOptions = {}): UseCanvassingSessionListResult => {
  const [items, setItems] = useState<CanvassingSessionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [calendarTimeZone, setCalendarTimeZone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CanvassingSessionRequestError | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_CANVASSING_SESSION_ROWS_PER_PAGE);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const filterKey = createFilterKey(filters);
  const previousFilterKey = useRef(filterKey);
  const filtersChanged = previousFilterKey.current !== filterKey;
  const requestPage = filtersChanged ? 0 : page;

  useEffect(() => {
    if (previousFilterKey.current !== filterKey) {
      previousFilterKey.current = filterKey;
      setPage(0);
    }
  }, [filterKey]);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setItems([]);
      setTotal(0);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const run = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchCanvassingSessions({
          filters,
          first: rowsPerPage,
          offset: requestPage * rowsPerPage,
          fetchImpl,
        });

        if (!cancelled) {
          setItems(data.canvassingSessionList.items);
          setTotal(data.canvassingSessionList.total);
          setCalendarTimeZone(data.canvassingSessionList.calendarTimeZone);

          const lastPage = Math.max(0, Math.ceil(data.canvassingSessionList.total / rowsPerPage) - 1);
          if (requestPage > lastPage) setPage(lastPage);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(toCanvassingSessionRequestError(requestError, 'Failed to load canvassing sessions.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [enabled, fetchImpl, filterKey, refetchCounter, requestPage, rowsPerPage]);

  const handleSetRowsPerPage = (nextRowsPerPage: number): void => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  };

  return {
    items,
    total,
    calendarTimeZone,
    loading: loading || filtersChanged,
    error,
    page: requestPage,
    rowsPerPage,
    setPage,
    setRowsPerPage: handleSetRowsPerPage,
    refetch: () => setRefetchCounter(counter => counter + 1),
  };
};
